import { mapLocalized, resolveLocalized } from "@/domain/localization";
import { validateAnswers, discoveryPricingStatus, type ExecutableDiscoveryModel, type RuntimeOffering, type OfferingSelection } from "@/domain/discovery-model";
import type { DiscoveryAnswers, DiscoveryResult, PricingFactor } from "@/domain/types";
import { isNumericPricing } from "@/domain/types";
import { resolveDiscoveryPath } from "@/domain/discovery-navigation";

export function matchesAnswer(answers: DiscoveryAnswers, factor: PricingFactor): boolean {
  const matches = (answerId?: string, values?: Array<string | number>): boolean => {
    if (!answerId) return true;
    const answer = answers[answerId];
    if (!values?.length) return answer !== undefined;
    return values.some((value) => Array.isArray(answer) ? answer.includes(String(value)) : answer === value);
  };
  return matches(factor.answerId, factor.answerValues) &&
    matches(factor.conditionAnswerId, factor.conditionAnswerValues);
}

function pricedRange(item: RuntimeOffering, answers: DiscoveryAnswers): { low: number; high: number; factors: PricingFactor[] } {
  if (!isNumericPricing(item.pricing)) throw new Error("Quote-required pricing has no numeric range.");
  let low = item.pricing.display === "fixed" ? item.pricing.fixed ?? item.pricing.low : item.pricing.low;
  let high = item.pricing.display === "fixed" ? item.pricing.fixed ?? item.pricing.high : item.pricing.high;
  const factors: PricingFactor[] = [];
  for (const factor of item.pricing.factors) {
    if (!matchesAnswer(answers, factor)) continue;
    factors.push(factor);
    if (factor.kind === "fixed") { low += factor.low ?? factor.amount ?? 0; high += factor.high ?? factor.amount ?? 0; }
    if (factor.kind === "per-unit" && factor.answerId) { const count = Number(answers[factor.answerId] ?? 0); low += count * (factor.low ?? factor.amount ?? 0); high += count * (factor.high ?? factor.amount ?? 0); }
    if (factor.kind === "multiplier") { low *= factor.multiplier ?? 1; high *= factor.multiplier ?? 1; }
  }
  return { low: Math.round(low), high: Math.round(high), factors };
}

function selectOffering(items: RuntimeOffering[], selection: OfferingSelection, answers: DiscoveryAnswers) {
  const answer = selection.kind === "question" ? answers[selection.questionId] : undefined;
  const id = selection.kind === "fixed" ? selection.offeringId : (typeof answer === "string" ? selection.options[answer] : undefined) ?? selection.fallbackId;
  return items.find((item) => item.id === id);
}

export function evaluateDiscovery(truth: ExecutableDiscoveryModel, answers: DiscoveryAnswers): DiscoveryResult {
  return evaluate(truth, answers, false);
}

/** Application-only subtotal. Never persist as an all-inclusive DiscoveryResult. */
export function evaluatePricedSubtotal(truth: ExecutableDiscoveryModel, answers: DiscoveryAnswers) {
  return evaluate(truth, answers, true).estimate;
}

function evaluate(truth: ExecutableDiscoveryModel, answers: DiscoveryAnswers, partial: boolean): DiscoveryResult {
  validateAnswers(truth, answers);
  const route = resolveDiscoveryPath(truth, answers);
  answers = route.answers;
  const product = selectOffering(truth.products, truth.selection.product, answers);
  const selectedService = selectOffering(truth.services, truth.selection.service, answers);
  const selected = [product, selectedService].filter((item): item is RuntimeOffering => Boolean(item));
  const pricingStatus = discoveryPricingStatus(truth, answers);
  const priced = selected.filter((item) => isNumericPricing(item.pricing)).map((item) => ({ item, range: pricedRange(item, answers) }));
  const withheld = truth.scenario.pricingDisplay === "hidden" || selected.some(item => isNumericPricing(item.pricing) && item.pricing.display === "hidden");
  if (pricingStatus && (!partial || withheld || !priced.length)) return {
    recommendedProductIds: product ? [product.id] : [], recommendedServiceIds: selectedService ? [selectedService.id] : [],
    summary: { en: "Your project details are ready for a personalized quote.", he: "פרטי הפרויקט מוכנים לקבלת הצעת מחיר אישית." },
    assumptions: [{ en: "The business will review your requirements and confirm pricing with you.", he: "העסק יבדוק את הדרישות ויתאם איתך את המחיר." }],
    estimate: null, pricingStatus, generatedAt: new Date(),
  };
  const low = priced.reduce((sum, item) => sum + item.range.low, 0);
  const high = priced.reduce((sum, item) => sum + item.range.high, 0);
  const required = route.path.map(({ question }) => question).filter((question) => question.required !== false);
  const answered = required.filter((question) => answers[question.id] !== undefined).length;
  const confidence = Math.min(95, Math.max(35, Math.round((answered / Math.max(1, required.length)) * 85 + 10)));
  const selectedOptions = route.path.flatMap(({ question }) => {
    if (!("options" in question)) return [];
    const answer = answers[question.id];
    const selected = Array.isArray(answer) ? answer : answer === undefined ? [] : [String(answer)];
    return question.options.filter((option) => selected.includes(option.id)).map((option) => option.label);
  });
  const includes = [...priced.map(({ item }) => item.name), ...(partial && selected.some(item => !isNumericPricing(item.pricing)) ? [] : selectedOptions), ...priced.flatMap(({ range }) => range.factors.map((factor) => factor.label))]
    .filter((item, index, all) => all.findIndex((candidate) => resolveLocalized(candidate, truth.business.primaryLanguage ?? "en") === resolveLocalized(item, truth.business.primaryLanguage ?? "en")) === index);
  return {
    recommendedProductIds: product ? [product.id] : [],
    recommendedServiceIds: selectedService ? [selectedService.id] : [],
    summary: product ? mapLocalized(product.name, (name, language) => language === "he" ? `${name} היא ההתאמה המומלצת לפי התשובות.` : `${name} is the strongest fit based on your answers.`) : { en: "An expert should review this request.", he: "מומלץ שמומחה יבדוק את הבקשה." },
    assumptions: [{ en: "Indicative estimate. Final pricing is confirmed by the business after review.", he: "הערכה ראשונית. המחיר הסופי יאושר על ידי העסק לאחר בדיקה." }],
    estimate: { low, high: Math.max(low, high), currency: truth.business.currency, confidence, includes },
    generatedAt: new Date(),
  };
}
